import React from 'react';
import {useSelector} from 'react-redux';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ComplexPolymerContentsQuery,
  ComplexPolymerContentsQueryVariables,
} from 'modbio/complex-polymers/__generated/ComplexPolymerContentsQuery';
import {
  ComplexPolymerType,
  COMPLEX_POLYMER_DISPLAY_NAMES,
  COMPLEX_POLYMER_CONTENTS_QUERY,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  isOligoConjugate,
  isOligoConjugateVersion,
  isOligoDuplex,
  isOligoDuplexVersion,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {capitalize} from 'util/NameUtil';

import OligoConjugatePolymerMap from './OligoConjugatePolymerMap';
import OligoDuplexPolymerMap from './OligoDuplexPolymerMap';

export default function ComplexPolymerMap(props: {complexPolymerId: string}): JSX.Element | null {
  const openComplexPolymerVersionId = useSelector(
    (state: BenchlingState) =>
      state.ui.modbio.complexPolymerVersionUIs[props.complexPolymerId].complexPolymerVersionId
  );

  return (
    <BenchlingQuery<ComplexPolymerContentsQuery, ComplexPolymerContentsQueryVariables>
      query={COMPLEX_POLYMER_CONTENTS_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      variables={{complexPolymerId: props.complexPolymerId, versionId: openComplexPolymerVersionId}}
    >
      {(data) => {
        if (isInaccessible(data.complexPolymer)) {
          return (
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text="You do not have permission to view this complex polymer"
            />
          );
        }

        if (isOligoConjugate(data.complexPolymer)) {
          assert(
            data.complexPolymer.version != null && isOligoConjugateVersion(data.complexPolymer.version),
            'Expected complex polymer version to exist'
          );

          if (data.complexPolymer.version.oligo == null) {
            return (
              <InlineBanner
                bannerType={InlineBannerType.WARNING}
                text={`${capitalize(
                  COMPLEX_POLYMER_DISPLAY_NAMES[ComplexPolymerType.OLIGO_CONJUGATE]
                )} structure is empty`}
              />
            );
          }
          const {bases, modifiedNucleotides, nucleotideType} = data.complexPolymer.version.oligo;

          return (
            <OligoConjugatePolymerMap
              versionId={data.complexPolymer.version.id}
              conjugates={data.complexPolymer.version.conjugates}
              branchedAttachments={data.complexPolymer.version.branchedAttachments}
              bases={bases}
              modifiedNucleotides={modifiedNucleotides}
              nucleotideType={nucleotideType}
              forCreate={false}
            />
          );
        }

        if (isOligoDuplex(data.complexPolymer)) {
          assert(
            data.complexPolymer.version != null && isOligoDuplexVersion(data.complexPolymer.version),
            'Expected complex polymer version to exist'
          );

          if (
            data.complexPolymer.version.forwardStrand == null ||
            data.complexPolymer.version.reverseStrand == null ||
            data.complexPolymer.version.offset == null
          ) {
            return (
              <InlineBanner
                bannerType={InlineBannerType.WARNING}
                text={`${capitalize(
                  COMPLEX_POLYMER_DISPLAY_NAMES[ComplexPolymerType.OLIGO_DUPLEX]
                )} structure cannot be visualized`}
              />
            );
          }

          return (
            <OligoDuplexPolymerMap
              versionId={data.complexPolymer.version.id}
              forwardStrand={data.complexPolymer.version.forwardStrand}
              reverseStrand={data.complexPolymer.version.reverseStrand}
              offset={data.complexPolymer.version.offset}
              forCreate={false}
            />
          );
        }

        assertAndContinue(false, 'Expected complex polymer entity');
        return <InlineBanner bannerType={InlineBannerType.ERROR} text="Entity is not a complex polymer" />;
      }}
    </BenchlingQuery>
  );
}
